// src/components/intake/IntakeIncomeScreen.js
//
// Ticket 064 (screens 2.2, 2.3s, 3.1, 3.1s). The income card grid for ONE owner
// and ONE set of category groups. It is the intake's own copy of the Calculator's
// Step 2 grid (page-private UI is copied, not imported, 053 D9) wrapping the
// IncomeCard (one optional slot prop added in amendment 1).
//   - the cap (D5): each card's onItemsChange is wrapped; the reducer refuses a
//     change that would take the total above MAX_INCOME_ITEMS and the notice
//     shows. A counter appears from ITEM_COUNTER_FROM items.
//   - a card the server suggests (and that is not entered yet) carries a small
//     "Suggested" mark. A suggestion never adds a card.
//   - "No ... income" button (no Skip, no "Not sure"): offered only while nothing
//     in the group has been filled in.
//   - the work-location question (D8) is rendered INSIDE each employment card
//     through IncomeCard's optional renderItemExtra slot (amendment 1).
//   - all four card groups are on this one page (amendment 2): "Suggested for you"
//     first (the server's order), a divider, then every other card type. The spouse has
//     her own page with the same layout. No per-card badge.
//   - "Skip income for now" (filer page only, while no card is touched).

import React from 'react'
import IncomeCard from '../../pages/IncomeCard'
import IntakeOptionButton from './IntakeOptionButton'
import IntakeWorkLocation from './IntakeWorkLocation'
import { CATEGORY_GROUPS } from '../../hooks/incomeCardConfig'
import {
  MAX_ITEMS, ITEM_COUNTER_FROM, LIMIT_NOTICE, SECTION23_SCREENS, SPOUSE_NONE_ERROR,
  SUGGESTED_BLOCK_TITLE, OTHER_BLOCK_TITLE, ALL_BLOCK_TITLE,
} from '../../lib/intake/intakeConfig'
import {
  CARD_SCREENS, cardTypesFor, touchedIn, totalItems, spouseTotal, suggestedCardTypes,
} from '../../lib/intake/section23Facts'

export default function IntakeIncomeScreen({ screenId, answers, suggestions, limitHit, onItemsChange, onNone, onSkip, onWorkLocation }) {
  const cfg = CARD_SCREENS[screenId]
  const text = SECTION23_SCREENS[screenId]
  const allCards = cardTypesFor(cfg.groups)
  // Block membership comes from the server's suggestions and does not change while
  // the person edits (a card must never jump blocks and remount). Server order is
  // the order: work, property, investments, retirement.
  const suggestedTypes = suggestedCardTypes(suggestions)
  const suggestedCards = suggestedTypes.map((t) => allCards.find((c) => c.income_type === t)).filter(Boolean)
  const otherCards = CATEGORY_GROUPS.flatMap((g) => allCards.filter((c) => c.category === g.key && !suggestedTypes.includes(c.income_type)))
  const total = totalItems(answers)
  const touchedNone = touchedIn(answers, cfg.owner, cfg.groups).length === 0
  const noneOffered = screenId === '2.2' && touchedNone
  const skipOffered = screenId === '2.2' && touchedNone
  const noneSelected = !!answers.none[cfg.noneKey]
  const spouseEmpty = screenId === '2.3s' && spouseTotal(answers) === 0 && !answers.skipIncome

  function renderCard(cardConfig) {
    const typeItems = answers.incomeItems.filter(
      (i) => i.income_type === cardConfig.income_type && (i.owner === 'spouse' ? 'spouse' : 'self') === cfg.owner,
    )
    return (
      <div key={cardConfig.income_type} className="intake-card-slot">
        <IncomeCard
          cardConfig={cardConfig}
          items={typeItems}
          onItemsChange={(items) => onItemsChange(cardConfig.income_type, cfg.owner, items)}
          owner={cfg.owner}
          renderItemExtra={cardConfig.income_type === 'employment' ? (item) => (
            <IntakeWorkLocation
              item={item}
              owner={cfg.owner}
              value={answers.workLocation[item.id]}
              onChange={onWorkLocation}
            />
          ) : undefined}
        />
      </div>
    )
  }

  return (
    <div className="intake-card-groups u-stack-sm">
      {suggestedCards.length > 0 && (
        <section className="intake-card-block" aria-label={SUGGESTED_BLOCK_TITLE}>
          <h2 className="u-label-base">{SUGGESTED_BLOCK_TITLE}</h2>
          <div className="cal-income-card-grid">{suggestedCards.map((c) => renderCard(c))}</div>
        </section>
      )}
      {suggestedCards.length > 0 && <hr className="intake-divider" />}
      <section className="intake-card-block" aria-label={OTHER_BLOCK_TITLE}>
        <h2 className="u-label-base">{suggestedCards.length > 0 ? OTHER_BLOCK_TITLE : ALL_BLOCK_TITLE}</h2>
        <div className="cal-income-card-grid">{otherCards.map((c) => renderCard(c))}</div>
      </section>

      {total >= ITEM_COUNTER_FROM && (
        <p className="intake-hint-line">{total} of {MAX_ITEMS} income items</p>
      )}
      {limitHit && <p className="intake-limit-note" role="alert">{LIMIT_NOTICE}</p>}

      {noneOffered && (
        <IntakeOptionButton
          label={text.none}
          selected={noneSelected}
          onClick={() => onNone(!noneSelected)}
        />
      )}
      {skipOffered && (
        <IntakeOptionButton
          label={text.skip}
          selected={!!answers.skipIncome}
          onClick={() => onSkip(!answers.skipIncome)}
        />
      )}
      {spouseEmpty && <p className="triage-submit-error">{SPOUSE_NONE_ERROR}</p>}
    </div>
  )
}
